import { useEffect, useState } from 'react';
import { Database, ShieldCheck, UserCheck, ArrowUpCircle, Check } from 'lucide-react';
import { globalEventBus, EVENTS } from '../services/eventBus';
import type { GraphNode } from '../types';

const GROUP_LABELS: Record<string, string> = {
  core: 'Kern',
  customer: 'Kunde',
  draft: 'Entwurf',
  unknown: 'Unbekannt',
};

export function EntityInspectorPanel() {
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [history, setHistory] = useState<string[]>([]);
  const [justPromoted, setJustPromoted] = useState(false);

  useEffect(() => {
    const unsub1 = globalEventBus.subscribe(EVENTS.GRAPH_UPDATE, (payload) => {
      const data = payload as { nodes: GraphNode[] };
      if (data.nodes.length === 0) {
        return;
      }
      setSelectedNode((current) => current ?? data.nodes[data.nodes.length - 1]);
      const newLabels = data.nodes.map((node) => node.label);
      setHistory((prev) => Array.from(new Set([...prev, ...newLabels])));
    });

    const unsub2 = globalEventBus.subscribe(EVENTS.NODE_SELECTED, (payload) => {
      const node = payload as GraphNode;
      setSelectedNode(node);
    });

    return () => {
      unsub1();
      unsub2();
    };
  }, []);

  const handlePromote = () => {
    if (!selectedNode) return;
    // TODO: Implement promoteDraftToCustomer method in ontologyManager
    // const success = ontologyManager.promoteDraftToCustomer(selectedNode.label);
    const success = false; // Placeholder until method is implemented
    if (success) {
      setJustPromoted(true);
      setTimeout(() => setJustPromoted(false), 1800);
    }
  };

  const getLayerColor = (layer: string) => {
    if (layer === 'core') return 'bg-sopho-entity/15 text-sopho-entity';
    if (layer === 'customer') return 'bg-sopho-process/15 text-sopho-process';
    if (layer === 'draft') return 'bg-sopho-rule/15 text-sopho-rule';
    return 'bg-destructive/15 text-destructive';
  };

  const getContainerStyle = (layer: string) => {
    if (layer === 'core') return 'bg-sopho-entity/10 border-sopho-entity/30';
    if (layer === 'customer') return 'bg-sopho-process/10 border-sopho-process/30';
    if (layer === 'draft') return 'bg-sopho-rule/10 border-sopho-rule/30';
    return 'bg-destructive/10 border-destructive/30';
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b border-border">
        <h3 className="text-sopho-entity font-bold uppercase text-[10px] tracking-widest flex items-center gap-2">
          <Database size={14} /> Entitäten-Inspektor
        </h3>
      </div>

      <div className="flex-1 p-3 overflow-y-auto">
        {selectedNode ? (
          <div className="space-y-4">
            <div className={`p-3 rounded-lg border shadow-lg ${getContainerStyle(selectedNode.group)}`}>
              <h2 className="text-lg font-bold text-foreground mb-1">{selectedNode.label}</h2>
              <div className="flex items-center gap-2 text-[10px] mt-2">
                <span className={`px-2 py-0.5 rounded font-bold uppercase tracking-wider ${getLayerColor(selectedNode.group)}`}>
                  {selectedNode.status ?? 'UNKNOWN'}
                </span>
                {selectedNode.group && (
                  <span className="text-muted-foreground uppercase opacity-75">Ebene: {GROUP_LABELS[selectedNode.group] ?? selectedNode.group}</span>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-[10px] uppercase text-muted-foreground font-bold">Metadaten</p>
              <div className="bg-muted/40 backdrop-blur-sm rounded p-3 text-xs space-y-2 border border-border">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Knoten-ID</span>
                  <span className="font-mono text-foreground text-[10px]">{selectedNode.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Typ</span>
                  <span className="text-foreground">{selectedNode.type ?? 'CONCEPT'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Stärke</span>
                  <span className="text-foreground">{selectedNode.val ?? 0}</span>
                </div>
              </div>
            </div>

            {(selectedNode.group === 'draft' || selectedNode.group === 'unknown') && (
              <div className="bg-muted/60 backdrop-blur-sm border border-border p-3 rounded-lg flex flex-col gap-2">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="text-sopho-entity mt-1 shrink-0" size={18} />
                  <div>
                    <p className="text-sm text-foreground font-bold">Freigabe erforderlich</p>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      Diese Entität wird der Kundenschicht (Grün) der aktuellen Domäne hinzugefügt.
                    </p>
                  </div>
                </div>
                <button
                  onClick={handlePromote}
                  disabled={justPromoted}
                  aria-label="Entität zur Kundenschicht freigeben"
                  className={`mt-2 flex items-center justify-center gap-2 w-full py-2 rounded text-xs font-semibold tracking-normal transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
                    ${justPromoted
                      ? 'bg-sopho-process text-white scale-95'
                      : 'bg-sopho-entity hover:bg-sopho-entity/90 text-white'}`}
                >
                  {justPromoted ? (
                    <>
                      <Check size={14} /> Freigegeben
                    </>
                  ) : (
                    <>
                      <ArrowUpCircle size={14} /> Zur Kundenschicht freigeben
                    </>
                  )}
                </button>
              </div>
            )}

            {selectedNode.group === 'customer' && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 p-3 rounded flex items-center gap-3">
                <UserCheck className="text-emerald-500" size={16} />
                <span className="text-xs text-emerald-300">Geprüftes Wissen für aktuelle Domäne.</span>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center text-muted-foreground mt-10 flex flex-col items-center">
            <div className="w-12 h-12 rounded-full bg-muted/40 flex items-center justify-center mb-4">
              <Database size={20} className="opacity-50" />
            </div>
            <p className="text-sm">Entität auswählen, um Details anzuzeigen</p>
            <p className="text-xs opacity-50 mt-1">Im Editor tippen, um Konzepte zu analysieren</p>
          </div>
        )}

        <div className="mt-6 pt-4 border-t border-border">
          <div className="flex justify-between items-center mb-3">
            <p className="text-[10px] uppercase text-muted-foreground font-bold">Sitzungsverlauf</p>
            <span className="text-[10px] text-muted-foreground bg-muted/40 border border-border px-1.5 rounded">
              {history.length}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {history.slice(-12).reverse().map((item) => (
              <span
                key={item}
                className="text-[10px] bg-muted/40 text-muted-foreground border border-border px-2 py-1 rounded"
              >
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
